import { useRef } from 'react'

import type { AgGridReact } from 'ag-grid-react'
import type { ReactElement } from 'react'
import type { DeviceType } from '~/types/device-type'
import type { IIapFeatures } from '~/types/iap'

import columns from './columns'

import TableWrapper from '~/components/Tables/TableWrapper'
import { FASTLY_PATH } from '~/const/asset'
import { useAppsManagementListQuery } from '~/queries/application-management'
import { useIapFeatureResourceQuery } from '~/queries/iap'
import { resize } from '~/utils/media'

interface IFeatureRowData {
  app: { app_icon_url?: string }
  deviceType: DeviceType
  feature: string
}

interface Props {
  appId?: string
  features?: IIapFeatures
  resourceId?: string
}

const FeaturesTable = ({ appId, features, resourceId }: Props): ReactElement => {
  /** Local state */

  const gridRef = useRef<AgGridReact<IFeatureRowData>>(null)

  const tableRef = useRef<HTMLDivElement>(null)

  /** Queries */

  const { data: apps } = useAppsManagementListQuery()

  const { data } = useIapFeatureResourceQuery({ appId, resourceId }, { enabled: !features })

  /** Render */

  const _features = features || data?.features

  const rowData = _features
    ? Object.entries(_features).reduce((prev, [appKey, featuresByDeviceType]) => {
        Object.entries(featuresByDeviceType).forEach(([deviceType, features]) => {
          const app_icon_url = apps?.find(({ app_key }) => app_key === appKey)?.icon_file_id

          features.forEach((feature) => {
            prev.push({
              app: {
                app_icon_url: app_icon_url ? resize(`${FASTLY_PATH}${app_icon_url}`, 32, 32) : undefined,
              },
              deviceType: deviceType as DeviceType,
              feature,
            })
          })
        })
        return prev
      }, [] as IFeatureRowData[])
    : undefined

  return (
    <TableWrapper
      className="features-table"
      columnDefs={columns}
      entriesName="features"
      ref={gridRef}
      rowData={rowData}
      suppressRowHoverHighlight
      tableRef={tableRef}
      withPagination={false}
      withSearchBar
    />
  )
}

export default FeaturesTable
